import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Scale } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { COMPANY as C } from "../constants/company";
import { LEGAL_SLUGS, getLegalDoc } from "../legal";

const Body = ({ items }) => {
  const out = [];
  let bullets = [];
  const flush = () => {
    if (bullets.length) out.push(<ul key={`ul-${out.length}`}>{bullets.map((b, i) => <li key={i}>{b}</li>)}</ul>);
    bullets = [];
  };
  items.forEach((it, i) => {
    if (it.startsWith("- ")) bullets.push(it.slice(2));
    else { flush(); out.push(<p key={i}>{it}</p>); }
  });
  flush();
  return out;
};

export default function Legal() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  if (!LEGAL_SLUGS.includes(slug)) return <Navigate to="/legal/terms" replace />;
  const doc = getLegalDoc(lang, slug);

  return (
    <main data-testid="legal-page" className="mx-auto max-w-7xl px-4 pb-28 pt-28 sm:px-6 lg:px-8">
      <div className="grid gap-12 lg:grid-cols-12">
        <aside className="lg:col-span-3">
          <div className="lg:sticky lg:top-24">
            <p className="eyebrow text-violet-600">{t.legal.title}</p>
            <p className="mt-2 text-sm text-ink-2">{t.legal.sub}</p>
            <p className="eyebrow mt-8 text-mist">{t.legal.toc}</p>
            <nav data-testid="legal-nav" className="mt-3 flex flex-col gap-1">
              {LEGAL_SLUGS.map((s) => (
                <Link key={s} to={`/legal/${s}`} data-testid={`legal-nav-${s}`} className={`rounded-xl px-3 py-2 text-sm transition-colors ${s === slug ? "bg-violet-100 font-semibold text-violet-800" : "text-ink-2 hover:bg-lilac hover:text-ink"}`}>
                  {t.legalTitles[s]}
                </Link>
              ))}
            </nav>
          </div>
        </aside>

        <motion.article key={slug + lang} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="lg:col-span-8 lg:col-start-5">
          <div className="flex flex-wrap items-center gap-3 text-xs text-mist">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-line bg-white px-2.5 py-1"><Scale size={12} /> {C.legalName}</span>
            <span data-testid="legal-updated">{t.legal.updated}: {C.legalUpdated}</span>
          </div>
          <h1 data-testid="legal-title" className="font-display mt-5 text-3xl font-extrabold leading-tight tracking-tight text-ink sm:text-4xl lg:text-5xl">{doc.title}</h1>
          <p className="mt-6 text-base leading-relaxed text-ink-2 sm:text-lg">{doc.intro}</p>
          <div className="legal-prose mt-4">
            {doc.sections.map((s) => (
              <section key={s.h}>
                <h2>{s.h}</h2>
                <Body items={s.body} />
              </section>
            ))}
          </div>
          <div data-testid="legal-disclaimer" className="mt-12 rounded-2xl border border-line bg-canvas p-5 text-xs leading-relaxed text-mist">{t.legal.disclaimer}</div>
        </motion.article>
      </div>
    </main>
  );
}
